/// <reference types="vite/client" />
import appCss from "@/App.css?url";
import baseCss from "@/styles.css?url";
import { getAuthInitializationScript, type AuthContextType } from "@/hooks/auth/auth";
import { getDarkModeInitializationScript } from "@/hooks/dark-mode/dark-mode";
import { RootAppShell } from "@/routes/-components/root-app-shell";
import { resolveRouteHeadMetadata } from "@/lib/route-title-metadata";
import { RootErrorComponent } from "@/routes/-components/root-error-component";
import { RootNotFoundComponent } from "@/routes/-components/root-not-found-component";
import type { QueryClient } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

export interface RouteContext {
  auth: AuthContextType;
  queryClient: QueryClient;
}

const RootDocument = ({ children }: { children: ReactNode }) => {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: getDarkModeInitializationScript() }} />
        <script dangerouslySetInnerHTML={{ __html: getAuthInitializationScript() }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
};

const RootLayout = () => {
  return (
    <RootDocument>
      <RootAppShell>
        <Outlet />
      </RootAppShell>
    </RootDocument>
  );
};

const RootRouteErrorComponent = (props: ErrorComponentProps) => {
  return (
    <RootDocument>
      <RootAppShell>
        <RootErrorComponent {...props} />
      </RootAppShell>
    </RootDocument>
  );
};

const deploymentNamePrefix = import.meta.env.PROD ? "" : "[Local] ";

export const Route = createRootRouteWithContext<RouteContext>()({
  head: ({ matches }) => {
    const routeHead = matches.length
      ? resolveRouteHeadMetadata(matches)
      : {
          title: `${deploymentNamePrefix}Veronics`,
          description: undefined,
          canonicalUrl: undefined,
          openGraphImageUrl: undefined,
        };

    return {
      links: [
        { rel: "stylesheet", href: baseCss },
        { rel: "stylesheet", href: appCss },
        ...(routeHead.canonicalUrl ? [{ rel: "canonical", href: routeHead.canonicalUrl }] : []),
      ],
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1" },
        { title: routeHead.title },
        ...(routeHead.description
          ? [
              { name: "description", content: routeHead.description },
              { property: "og:title", content: routeHead.title },
              { property: "og:description", content: routeHead.description },
              { property: "og:type", content: "website" },
              ...(routeHead.openGraphImageUrl ? [{ property: "og:image", content: routeHead.openGraphImageUrl }] : []),
            ]
          : []),
      ],
    };
  },
  component: RootLayout,
  errorComponent: RootRouteErrorComponent,
  notFoundComponent: RootNotFoundComponent,
});
